import { getListPrice } from "~/domains/product/utils/productDescriptorUtils";

import type { ReactNode } from "react";
import { styled } from "~/common/mui";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { PriceElements } from "~/domains/pricing/components/Price/PriceV4";

interface ListPriceProps {
  product: ProductDescriptor;
  packCode: string;
  hidden?: boolean;
}

export const ListPrice = ({ product, packCode, hidden }: ListPriceProps) => {
  const price = getListPrice({ product, packCode });
  if (!price || !price.value) {
    return null;
  }
  return (
    <PriceElements.Stack gap={0}>
      <PriceElements.Price price={price} />
      <PriceElements.ListPriceLabel unit={price.unit ?? 1} color="textSecondary" />
    </PriceElements.Stack>
  );
};

export const CardContent = styled("div")(({ theme }) => ({
  padding: theme.spacing(3),
  [theme.breakpoints.up("desktop-s")]: {
    paddingBlock: theme.spacing(2),
    paddingInline: theme.spacing(2),
  },
  [theme.breakpoints.up("desktop-m")]: {
    paddingInline: theme.spacing(4),
  },
  [theme.breakpoints.up("desktop-l")]: {
    paddingInline: theme.spacing(8),
  },
}));

interface ProductDetailsLayoutProps {
  className?: string;
  imageSlot: ReactNode;
  productDataSlot: ReactNode;
  priceDataSlot: ReactNode;
  actionSlot: ReactNode;
  techTableSlot: ReactNode;
  buttonsSlot: ReactNode;
}

export const ProductDetailsLayout = styled(
  ({
    className,
    imageSlot,
    productDataSlot,
    priceDataSlot,
    actionSlot,
    techTableSlot,
    buttonsSlot,
  }: ProductDetailsLayoutProps) => {
    return (
      <div className={className}>
        {imageSlot && <div className="image-slot">{imageSlot}</div>}
        {productDataSlot && <div className="product-data-slot">{productDataSlot}</div>}
        {priceDataSlot && <div className="price-data-slot">{priceDataSlot}</div>}
        {actionSlot && <div className="action-slot">{actionSlot}</div>}
        {techTableSlot && <div className="tech-table-slot">{techTableSlot}</div>}
        {buttonsSlot && <div className="buttons-slot">{buttonsSlot}</div>}
      </div>
    );
  },
)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),

  [theme.breakpoints.up("tablet")]: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
  },

  [theme.breakpoints.up("desktop-s")]: {
    gridTemplateColumns: "160px 290px minmax(0, 1fr) 220px",
    rowGap: theme.spacing(2),
    columnGap: theme.spacing(3),
  },

  [theme.breakpoints.up("desktop-m")]: {
    columnGap: theme.spacing(4),
  },

  [".image-slot"]: {
    gridRow: "1",
    gridColumn: "1",
  },

  [".product-data-slot"]: {
    gridRow: "2",
    gridColumn: "1",

    [theme.breakpoints.up("desktop-s")]: {
      gridRow: "1",
      gridColumn: "2",
    },
  },

  [".price-data-slot"]: {
    gridRow: "1",
    gridColumn: "2",

    [theme.breakpoints.up("desktop-s")]: {
      gridColumn: "3",
    },
  },

  [".action-slot"]: {
    gridRow: "2",
    gridColumn: "2",

    [theme.breakpoints.up("desktop-s")]: {
      gridRow: "1",
      gridColumn: "4",
    },
  },

  [".tech-table-slot"]: {
    gridColumn: "1 / 3",

    [theme.breakpoints.up("desktop-s")]: {
      gridColumn: "2 / 4",
    },
  },

  [".buttons-slot"]: {
    gridColumn: "1 / 3",

    [theme.breakpoints.up("desktop-s")]: {
      alignSelf: "flex-end",
      gridColumn: "4 / 5",
    },
  },
}));
